import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Eye, CheckCircle, RefreshCw } from "lucide-react";
import { BoltIcon } from "../components/Bolts";
import AdminNav from "../components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRequestsPage = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/custom-requests`);
            setRequests(res.data || []);
        } catch (e) {
            toast.error("Failed to load requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const deleteRequest = async (id) => {
        if (!window.confirm("Delete this request permanently?")) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/custom-requests/${id}`, {
                auth: { username: "admin", password: window.prompt("Admin password:") }
            });
            toast.success("Request deleted");
            reload();
            setSelectedRequest(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally { setBusy(false); }
    };

    const markCompleted = async (id) => {
        setBusy(true);
        try {
            await axios.post(`${API}/custom-requests/${id}/mark-completed`, {}, {
                auth: { username: "admin", password: window.prompt("Admin password:") }
            });
            toast.success("Marked as completed");
            reload();
            setSelectedRequest(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally { setBusy(false); }
    };

    const isDone = (r) => r.status === "completed";

    return (
        <div data-testid="admin-requests-page" className="min-h-[80vh]">
            <section className="border-b border-white/10 bg-storm-navy">
                <div className="container-x py-16 md:py-20 flex items-end justify-between flex-wrap gap-6">
                    <div>
                        <p className="eyebrow flex items-center gap-2 mb-4">
                            <BoltIcon className="w-3.5 h-3.5" /> Admin
                        </p>
                        <h1 className="text-4xl md:text-6xl text-white leading-[0.9]">
                            <span className="text-storm-bolt">Requests</span>
                        </h1>
                    </div>
                    <button
                        onClick={reload}
                        className="btn-secondary !text-sm !px-5 !py-3"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </section>

            <AdminNav active="requests" />

            <section className="container-x pt-10 pb-20 space-y-4">
                {loading ? (
                    <p className="text-storm-mist">Loading...</p>
                ) : requests.length === 0 ? (
                    <p className="text-storm-mist">No custom requests.</p>
                ) : (
                    <div className="overflow-x-auto border border-white/10">
                        <table className="w-full text-sm">
                            <thead className="bg-storm-void border-b border-white/10">
                                <tr>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Date</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Name</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Size</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Description</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Status</th>
                                    <th className="text-left px-4 py-3 text-storm-bolt uppercase tracking-wider font-heading text-xs">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((req) => (
                                    <tr key={req.id} className="border-b border-white/10 hover:bg-storm-void/50 cursor-pointer" onClick={() => setSelectedRequest(req)}>
                                        <td className="px-4 py-3 text-white">{new Date(req.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-white">{req.name}</td>
                                        <td className="px-4 py-3 text-storm-mist text-xs">
                                            {[req.length_in && `${req.length_in}"L`, req.width_in && `${req.width_in}"W`].filter(Boolean).join(" × ") || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-storm-mist truncate max-w-[200px]">{req.description}</td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs uppercase px-2 py-1 border ${isDone(req) ? "border-green-600/30 text-green-400" : "border-yellow-600/30 text-yellow-400"}`}>
                                                {isDone(req) ? "Done" : "Pending"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={() => setSelectedRequest(req)} className="text-storm-bolt hover:text-white p-1" title="View details">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => deleteRequest(req.id)} disabled={busy} className="text-red-400 hover:text-red-300 p-1 disabled:opacity-50">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {selectedRequest && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedRequest(null)}>
                        <div className="bg-storm-navy border border-white/10 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-start mb-6">
                                <h2 className="font-heading text-2xl text-white uppercase tracking-wider">Request from {selectedRequest.name}</h2>
                                <button onClick={() => setSelectedRequest(null)} className="text-storm-mist hover:text-white text-2xl">&times;</button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-storm-mist text-xs uppercase mb-1">Date</p>
                                        <p className="text-white">{new Date(selectedRequest.created_at).toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-storm-mist text-xs uppercase mb-1">Status</p>
                                        <p className="text-white">{isDone(selectedRequest) ? "Completed" : "Pending"}</p>
                                    </div>
                                    {(selectedRequest.length_in || selectedRequest.width_in) && (
                                        <div>
                                            <p className="text-storm-mist text-xs uppercase mb-1">Size</p>
                                            <p className="text-white">
                                                {[selectedRequest.length_in && `${selectedRequest.length_in}in L`, selectedRequest.width_in && `${selectedRequest.width_in}in W`].filter(Boolean).join(" × ")}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="border-t border-white/10 pt-4">
                                    <p className="text-storm-mist text-xs uppercase mb-2">Email</p>
                                    <p className="text-white">{selectedRequest.email}</p>
                                </div>

                                <div className="border-t border-white/10 pt-4">
                                    <p className="text-storm-mist text-xs uppercase mb-2">Phone</p>
                                    <p className="text-white">{selectedRequest.phone || "—"}</p>
                                </div>

                                {selectedRequest.address && (
                                    <div className="border-t border-white/10 pt-4">
                                        <p className="text-storm-mist text-xs uppercase mb-2">Address</p>
                                        <p className="text-white whitespace-pre-line">{selectedRequest.address}</p>
                                    </div>
                                )}

                                <div className="border-t border-white/10 pt-4">
                                    <p className="text-storm-bolt text-sm uppercase tracking-wider mb-3 font-heading">Description</p>
                                    <p className="text-white whitespace-pre-wrap">{selectedRequest.description}</p>
                                </div>

                                {(selectedRequest.image_ids || []).length > 0 && (
                                    <div className="border-t border-white/10 pt-4">
                                        <p className="text-storm-mist text-xs uppercase mb-2">Photos</p>
                                        <div className="flex flex-wrap gap-3">
                                            {selectedRequest.image_ids.map((imgId) => (
                                                <a key={imgId} href={`${API}/custom-requests/image/${imgId}`} target="_blank" rel="noreferrer"
                                                    className="block w-24 h-24 border border-white/10 hover:border-storm-bolt">
                                                    <img src={`${API}/custom-requests/image/${imgId}`} alt="" className="w-full h-full object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex gap-3 pt-6 border-t border-white/10">
                                {!isDone(selectedRequest) && (
                                    <button onClick={() => markCompleted(selectedRequest.id)} disabled={busy} className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 font-heading uppercase text-sm disabled:opacity-50">
                                        <CheckCircle className="w-4 h-4 inline mr-2" />
                                        Mark Completed
                                    </button>
                                )}
                                <button onClick={() => deleteRequest(selectedRequest.id)} disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 font-heading uppercase text-sm disabled:opacity-50">
                                    <Trash2 className="w-4 h-4 inline mr-2" />
                                    Delete
                                </button>
                                <button onClick={() => setSelectedRequest(null)} className="flex-1 bg-storm-void border border-white/10 text-white px-4 py-2 font-heading uppercase text-sm hover:bg-storm-navy">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default AdminRequestsPage;
